import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const Privacy = () => {
  const navigate = useNavigate();
  return (
  <div className="min-h-screen bg-background px-6 py-12">
    <SEOHead title="Privacy Notice — Dearestly" description="How Dearestly protects your data. No social features, no public profiles, and your data is never shared or sold." path="/privacy" />
    <div className="mx-auto max-w-2xl">
      <button onClick={() => navigate(-1)} className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
      <p className="mb-1 text-sm font-medium text-muted-foreground">Dearestly.</p>
      <h1 className="mb-2 font-serif text-4xl font-semibold text-foreground">Privacy Notice</h1>
      <p className="mb-10 text-sm text-muted-foreground">Last updated: 10 April 2026</p>

      <div className="space-y-10 text-muted-foreground leading-relaxed">
        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">1. Who We Are</h2>
          <p>Kena Technologies Limited ('we', 'us', 'our') operates Dearestly at dearestly.co — a relationship platform that helps you remember the people who matter, reach out with personal messages, and send curated gifts. We handle your personal data with care and in line with applicable Hong Kong data protection laws.</p>
          <p className="mt-3">Contact: <a href="mailto:hello@circle.dearestly.co" className="underline underline-offset-4 text-foreground">hello@circle.dearestly.co</a></p>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">2. What Data We Collect</h2>

          <h3 className="mt-4 mb-2 font-medium text-foreground">2.1 Account Data</h3>
          <p>When you create an account: your name, email address, and password (hashed — never stored in plain text).</p>

          <h3 className="mt-4 mb-2 font-medium text-foreground">2.2 Relationship Data</h3>
          <p>Information about people in your Dearestly: their names, birthdays, relationship type, occasions, and any personal notes, personality traits, or shared memories you choose to add. This data is used solely to power reminders and message generation for you.</p>

          <h3 className="mt-4 mb-2 font-medium text-foreground">2.3 WhatsApp Number (Optional, Dearestly Plus Only)</h3>
          <p>If you enable WhatsApp reminders, we collect your WhatsApp phone number solely for reminder delivery via Twilio. Collection is optional. You may withdraw consent at any time via Settings → Reminders. Your number is never used for marketing.</p>

          <h3 className="mt-4 mb-2 font-medium text-foreground">2.4 Gift Order Data</h3>
          <p>When you place a gift order through Dearestly, we collect: the recipient's name, your personalisation details (message, photo description, or customisation notes), and the delivery address. This information is used solely to fulfil your order. It is shared with our fulfilment partners only as necessary to produce and deliver the gift.</p>

          <h3 className="mt-4 mb-2 font-medium text-foreground">2.5 Payment Data</h3>
          <p>Payments are processed by Stripe. We do not store your card details. Stripe's privacy policy applies to payment processing.</p>

          <h3 className="mt-4 mb-2 font-medium text-foreground">2.6 Usage Data</h3>
          <p>Login times, features used, and general app activity to improve the product.</p>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">3. How We Use Your Data</h2>
          <ul className="mt-2 list-disc pl-6 space-y-1">
            <li>To deliver birthday and occasion reminders</li>
            <li>To generate personalised messages using AI</li>
            <li>To fulfil gift orders you place through the app</li>
            <li>To send transactional emails (welcome, reminders, order confirmations)</li>
            <li>To manage your account and provide support</li>
            <li>To improve our services</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">4. Gift Orders — Data Sharing for Fulfilment</h2>
          <p>When you place a gift order, the following data is shared with our fulfilment partners solely for the purpose of producing and delivering your order: recipient name, delivery address, and personalisation details. Fulfilment partners are contractually required to handle this data only for fulfilment purposes and to delete it once the order is complete.</p>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">5. Third-Party Services</h2>
          <ul className="mt-2 list-disc pl-6 space-y-1">
            <li>Supabase — database and authentication infrastructure</li>
            <li>Resend — transactional email delivery</li>
            <li>Twilio — WhatsApp reminder delivery (Plus subscribers only)</li>
            <li>Stripe — payment processing for Dearestly Plus subscriptions and gift orders</li>
            <li>AI content generation — used to generate personalised messages</li>
            <li>Lovable — development and hosting platform</li>
          </ul>
          <p className="mt-3">We do not sell your personal data to any third party.</p>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">6. Data About Third Parties</h2>
          <p>When you add someone to Dearestly, you confirm you have a legitimate personal reason to store their information. We do not contact the people in your Dearestly directly, and we do not share their information beyond what is necessary to fulfil a gift order you have explicitly placed.</p>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">7. Data Retention</h2>
          <p>We retain your data for as long as your account is active. On account deletion, all personal data including contacts, notes, WhatsApp number, and order history is deleted immediately. Gift order data required for active fulfilment is retained until the order is complete.</p>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">8. Your Rights</h2>
          <p>Under applicable Hong Kong data protection laws, you have the right to access, correct, or delete your personal data. Contact us at <a href="mailto:hello@circle.dearestly.co" className="underline underline-offset-4 text-foreground">hello@circle.dearestly.co</a>. We will respond within 40 days.</p>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">9. Security</h2>
          <p>We use encrypted data storage, HTTPS, row-level access controls, and authenticated API access. Payment data is handled entirely by Stripe and never touches our servers.</p>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">10. Changes to This Notice</h2>
          <p>We may update this notice periodically. Continued use of Dearestly constitutes acceptance of the updated notice.</p>
        </section>

        <section>
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">11. Contact</h2>
          <div className="mt-3">
            <p className="font-medium text-foreground">Kena Technologies Limited</p>
            <p>Email: <a href="mailto:hello@circle.dearestly.co" className="underline underline-offset-4 text-foreground">hello@circle.dearestly.co</a></p>
            <p>Website: <a href="https://dearestly.co" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 text-foreground">dearestly.co</a></p>
          </div>
        </section>

        <p className="text-xs text-muted-foreground pt-4 border-t border-border">© 2026 Kena Technologies Limited. All rights reserved.</p>
      </div>
    </div>
  </div>
  );
};

export default Privacy;
